import { useEffect, useRef } from 'react';
import {
  type CameraSlice,
  createStoreHooks,
  getCoordinateTransformContext,
} from '../../../../canvas/core';
import {
  calculateViewportInContent,
  findMostVisiblePage,
  type PageLayoutData,
  type PageLayoutSlice,
} from '../../../core';

type UseActivePageTrackingDeps = CameraSlice & PageLayoutSlice;

const { useStoreApi } = createStoreHooks<UseActivePageTrackingDeps>();

/**
 * Tracks which page is most visible in the viewport and sets it as active.
 *
 * Uses imperative `store.subscribe` instead of `useStore` to avoid
 * subscribing the calling component to camera state changes, which
 * would cause per-frame re-renders during pan/zoom.
 */
export const useActivePageTracking = (pageLayouts: PageLayoutData[]) => {
  const store = useStoreApi();
  const pageLayoutsRef = useRef(pageLayouts);
  pageLayoutsRef.current = pageLayouts;

  const prevActivePageRef = useRef<number | null>(null);

  useEffect(() => {
    let rafId = 0;

    const runUpdate = () => {
      const state = store.getState();
      const transformCtx = getCoordinateTransformContext(state.canvas);
      const viewport = calculateViewportInContent(transformCtx);

      const mostVisiblePage = findMostVisiblePage(
        pageLayoutsRef.current,
        viewport,
        state.pageLayout.activeId
      );

      const newActivePageId = mostVisiblePage?.pageId ?? null;

      if (newActivePageId !== prevActivePageRef.current) {
        state.pageLayout.actions.setActivePage(newActivePageId);
        prevActivePageRef.current = newActivePageId;
      }
    };

    const scheduleUpdate = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(runUpdate);
    };

    // Run once immediately
    scheduleUpdate();

    // Only recompute when the camera transform changes — ignore unrelated
    // store writes (e.g. page visibility, translation layer, etc.).
    const unsubscribe = store.subscribe((state, prevState) => {
      if (state.canvas.transform !== prevState.canvas.transform) {
        scheduleUpdate();
      }
    });

    return () => {
      unsubscribe();
      cancelAnimationFrame(rafId);
    };
  }, [store]);
};
